import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Modal,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Notiflix from "notiflix";
import useIndex from "../../../../../hooks/useIndex";
import useWompiCheckout from "../../../../../hooks/useWompiCheckout";

const PRICE_PER_MOVE_COP = 1000;
const MIN_MOVES_TO_BUY = 5;

/**
 * Modal de Recargar Jugadas con dos secciones visibles a la vez:
 *   - Comprar jugadas (mínimo 5, abre el widget de Wompi al confirmar).
 *   - Tengo un código (validación contra el back para asignar jugadas).
 */
const RechargeCodeModal = ({
  open,
  onClose,
  gameCode,
  setGameCode,
  onSubmit,
  loading = false,
  successTick = 0,
}) => {
  const { auth, token } = useIndex();
  const [movesToBuy, setMovesToBuy] = useState(MIN_MOVES_TO_BUY);

  const { openCheckout, loading: wompiLoading } = useWompiCheckout({
    closeParentModal: onClose,
  });

  // Cuando el padre dice "éxito" -> limpiar código y cerrar.
  useEffect(() => {
    if (!open) return;
    if (!successTick) return;
    setGameCode("");
    onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successTick]);

  const trimmedCode = useMemo(() => (gameCode ?? "").trim(), [gameCode]);
  const canSubmitCode = !!trimmedCode && !loading;

  const handleSubmitCode = async (e) => {
    e.preventDefault();
    if (!canSubmitCode) return;
    await onSubmit?.(trimmedCode);
  };

  const handleClose = () => onClose?.();

  const totalCop = (Number(movesToBuy) || 0) * PRICE_PER_MOVE_COP;
  const isAuthenticated = Boolean(auth && token);
  const canBuy = !wompiLoading && Number(movesToBuy) >= MIN_MOVES_TO_BUY && isAuthenticated;

  const handleBuyPlays = () => {
    if (!isAuthenticated) {
      Notiflix.Notify.warning(
        "Debes iniciar sesión para comprar jugadas."
      );
      return;
    }
    if (!canBuy) return;
    const playsLabel = movesToBuy === 1 ? "jugada" : "jugadas";
    openCheckout({
      description: `Compra de ${movesToBuy} ${playsLabel}`,
      invoice_origin: "Bingo",
      lines: [
        {
          description: `${movesToBuy} ${playsLabel} de Bingo Fenabocol`,
          product_name: "Jugada Bingo",
          amount: totalCop * 100, // a centavos
        },
      ],
    });
  };

  const decrementMoves = () =>
    setMovesToBuy((n) => Math.max(MIN_MOVES_TO_BUY, (Number(n) || MIN_MOVES_TO_BUY) - 1));
  const incrementMoves = () => setMovesToBuy((n) => (Number(n) || 0) + 1);
  const onMovesChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setMovesToBuy("");
      return;
    }
    setMovesToBuy(Math.max(1, Math.floor(Number(raw) || 0)));
  };
  const onMovesBlur = () => {
    if (!movesToBuy || movesToBuy < MIN_MOVES_TO_BUY) {
      setMovesToBuy(MIN_MOVES_TO_BUY);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} keepMounted>
      <Center>
        <Card>
          <Header>
            <Typography className="title">Recargar Jugadas</Typography>
            <IconButton onClick={handleClose} className="closeBtn" size="small">
              <CloseIcon />
            </IconButton>
          </Header>

          {auth && token && (
            <CurrentMoves>
              Tienes <strong>{auth?.moves ?? 0}</strong> jugadas para realizar
            </CurrentMoves>
          )}

          {/* ===== Validar código ===== */}
          <Section>
            <SectionTitle>Tengo un código</SectionTitle>
            <Hint>Pega tu código para validarlo y recibir las jugadas.</Hint>

            <form onSubmit={handleSubmitCode}>
              <CodeRow>
                <CodeField
                  fullWidth
                  size="small"
                  placeholder="ABCD-1234-XXXX..."
                  value={gameCode}
                  disabled={loading}
                  autoComplete="off"
                  onChange={(e) => setGameCode(e.target.value.toUpperCase())}
                  inputProps={{
                    inputMode: "text",
                    autoCapitalize: "characters",
                    autoCorrect: "off",
                    spellCheck: false,
                  }}
                />
                <SecondaryBtn type="submit" disabled={!canSubmitCode}>
                  {loading ? "..." : "Validar"}
                </SecondaryBtn>
              </CodeRow>
            </form>
          </Section>

          <OrSeparator>
            <span>o</span>
          </OrSeparator>

          {/* ===== Comprar jugadas ===== */}
          <Section>
            <SectionTitle>Comprar jugadas</SectionTitle>
            <Hint>
              Mínimo {MIN_MOVES_TO_BUY} jugadas. Cada una cuesta{" "}
              <strong>${PRICE_PER_MOVE_COP.toLocaleString("es-CO")} COP</strong>.
            </Hint>

            <NumberControl>
              <StepperBtn
                type="button"
                onClick={decrementMoves}
                disabled={wompiLoading || movesToBuy <= MIN_MOVES_TO_BUY}
                aria-label="Disminuir cantidad"
              >
                <RemoveIcon fontSize="small" />
              </StepperBtn>
              <NumberField
                type="number"
                min={MIN_MOVES_TO_BUY}
                value={movesToBuy}
                disabled={wompiLoading}
                onChange={onMovesChange}
                onBlur={onMovesBlur}
                inputMode="numeric"
              />
              <StepperBtn
                type="button"
                onClick={incrementMoves}
                disabled={wompiLoading}
                aria-label="Aumentar cantidad"
              >
                <AddIcon fontSize="small" />
              </StepperBtn>
            </NumberControl>

            <TotalRow>
              <span className="label">Total a pagar</span>
              <span className="value">
                ${Number(totalCop || 0).toLocaleString("es-CO")} COP
              </span>
            </TotalRow>

            {!isAuthenticated && (
              <AuthNotice>
                Para comprar jugadas debes iniciar sesión o crear una cuenta.
              </AuthNotice>
            )}

            <PrimaryBtn
              type="button"
              onClick={handleBuyPlays}
              disabled={!canBuy}
            >
              {wompiLoading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Pagar con Wompi"
              )}
            </PrimaryBtn>
          </Section>
        </Card>
      </Center>
    </Modal>
  );
};

export default RechargeCodeModal;

/* ===================== styles ===================== */

const Center = styled.div`
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
`;

const Card = styled.div`
  width: min(560px, 44vw);
  max-width: 92vw;

  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 22px 55px rgba(0, 0, 0, 0.45);

  padding: 18px 20px 20px;

  @media (max-width: 700px) {
    width: 92vw;
    padding: 16px 14px 18px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 4px;

  .title {
    font-weight: 900;
    font-size: clamp(1.2rem, 2.4vw, 1.7rem);
    line-height: 1.05;
    color: #1a1d28;
  }

  .closeBtn {
    border: 2px solid rgba(0, 0, 0, 0.2);
    border-radius: 10px;
  }
`;

const CurrentMoves = styled.div`
  text-align: center;
  font-weight: 600;
  font-size: 0.95rem;
  color: rgba(26, 29, 40, 0.78);
  background: rgba(116, 63, 227, 0.08);
  border-radius: 10px;
  padding: 6px 10px;
  margin: 8px 0 14px;

  strong {
    color: #743fe3;
    font-weight: 900;
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 900;
  color: #1a1d28;
  letter-spacing: 0.01em;
`;

const Hint = styled.p`
  margin: 0;
  color: rgba(26, 29, 40, 0.65);
  font-weight: 500;
  font-size: 0.85rem;
  line-height: 1.35;

  strong {
    color: #1a1d28;
    font-weight: 800;
  }
`;

const NumberControl = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr 44px;
  gap: 8px;
  align-items: stretch;
  width: 100%;
  margin-top: 4px;
`;

const StepperBtn = styled.button`
  border: 2px solid #1a1d28;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  height: 40px;
  transition: filter 0.12s ease, transform 0.12s ease;
  padding: 0;

  svg {
    color: #1a1d28;
    font-size: 18px;
  }

  &:hover {
    filter: brightness(1.03);
  }
  &:active {
    transform: scale(0.96);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const NumberField = styled.input`
  width: 100%;
  height: 40px;
  border-radius: 10px;
  border: 2px solid #1a1d28;
  text-align: center;
  font-weight: 900;
  font-size: 1.1rem;
  color: #1a1d28;
  background: #fff;
  -moz-appearance: textfield;
  padding: 0 6px;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(116, 63, 227, 0.18);
  }
`;

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  padding: 10px 14px;
  background: #f6f3ff;
  border-radius: 10px;

  .label {
    font-size: 0.85rem;
    font-weight: 800;
    color: rgba(26, 29, 40, 0.65);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .value {
    font-weight: 900;
    color: #1a1d28;
    font-size: 1.1rem;
  }
`;

const OrSeparator = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 18px 0 14px;
  color: rgba(26, 29, 40, 0.45);
  font-weight: 800;
  font-size: 0.8rem;
  text-transform: uppercase;

  span {
    flex: 0 0 auto;
  }

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(0, 0, 0, 0.1);
  }
`;

const CodeRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const CodeField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 10px;
    background: #fff;
    font-weight: 700;

    .MuiOutlinedInput-notchedOutline {
      border: 2px solid #1a1d28;
    }

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: #1a1d28;
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: #1a1d28;
      box-shadow: 0 0 0 3px rgba(116, 63, 227, 0.18);
    }
  }

  input {
    height: 40px;
    padding: 0 12px;
    font-size: 0.95rem;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }
`;

const PrimaryBtn = styled.button`
  width: 100%;
  height: 46px;
  border: 0;
  border-radius: 10px;
  cursor: pointer;

  background: #743fe3;
  color: #ffffff;

  font-weight: 900;
  font-size: 1rem;

  display: grid;
  place-items: center;

  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.14);
  transition: filter 0.12s ease, transform 0.12s ease;
  margin-top: 4px;

  &:hover {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }
`;

const SecondaryBtn = styled.button`
  height: 44px;
  min-width: 100px;
  padding: 0 16px;
  border: 0;
  border-radius: 10px;
  cursor: pointer;

  background: #1a1d28;
  color: #ffffff;

  font-weight: 900;
  font-size: 0.95rem;

  transition: filter 0.12s ease, transform 0.12s ease;

  &:hover {
    filter: brightness(1.1);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const AuthNotice = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(211, 47, 47, 0.08);
  border: 1px solid rgba(211, 47, 47, 0.25);
  color: #c62828;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: center;
`;
